/** Undo/redo over immutable snapshots (structural sharing makes snapshots cheap). Rapid edits with the same
 *  coalesce key (a drag, a slider, typing) become ONE undo step. */
export interface Entry<T> { value: T; key: string | null; at: number }

export class History<T> {
  private past: Entry<T>[] = [];
  private future: T[] = [];
  present: T;
  private presentKey: string | null = null;
  private presentAt = 0;
  private readonly windowMs: number;
  private readonly limit: number;

  constructor(initial: T, windowMs = 700, limit = 200) {
    this.present = initial; this.windowMs = windowMs; this.limit = limit;
  }
  get canUndo(): boolean { return this.past.length > 0; }
  get canRedo(): boolean { return this.future.length > 0; }

  push(next: T, key: string | null = null, now = Date.now()): void {
    if (next === this.present) return;
    const merge = key !== null && key === this.presentKey && now - this.presentAt <= this.windowMs && this.past.length > 0;
    if (!merge) {
      this.past.push({ value: this.present, key: this.presentKey, at: this.presentAt });
      if (this.past.length > this.limit) this.past.shift();
    }
    this.present = next; this.presentKey = key; this.presentAt = now; this.future = [];
  }
  undo(): T {
    const e = this.past.pop();
    if (!e) return this.present;
    this.future.push(this.present);
    this.present = e.value; this.presentKey = null; this.presentAt = 0;
    return this.present;
  }
  redo(): T {
    const v = this.future.pop();
    if (v === undefined) return this.present;
    this.past.push({ value: this.present, key: null, at: 0 });
    this.present = v; this.presentKey = null; this.presentAt = 0;
    return this.present;
  }
  /** Replace the present without creating an undo step (e.g. adopting a server-assigned value). */
  replacePresent(v: T): void { this.present = v; }
}
